/**
 * Patient Sharing Service
 * 
 * Handles patient sharing between users within the same company.
 * Standard-role owners and above manage shares through the validated backend.
 */

import { db } from './firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
} from 'firebase/firestore';
import { getData } from './firestoreService';
import { getAuthHeaders } from './apiUtils';

/**
 * Fire-and-forget: re-index patient + documents in the search index after sharing changes.
 */
async function reindexSharingInSearch(patientId) {
  try {
    const headers = await getAuthHeaders();
    fetch('/api/search/reindex-sharing', {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ patientId }),
    }).catch(() => {});
  } catch {
    // Non-critical — search index will be eventually consistent on next ingestion
  }
}

/**
 * Create a new patient share
 * @param {Object} shareData - Share configuration
 * @param {string} shareData.patientId - The patient being shared
 * @param {string} shareData.ownerId - The patient owner's userId
 * @param {string} shareData.sharedWithUserId - User receiving access
 * @param {string} shareData.accessLevel - "view" | "view_add" | "full"
 * @param {string} shareData.sharedBy - Super admin creating the share
 * @param {string} shareData.companyId - Company both users belong to
 * @param {Date|null} shareData.expiresAt - Optional expiration date
 * @param {string} [shareData.patientName] - Optional patient display name for recipient notification
 * @returns {Promise<string>} The new share document ID
 */
async function submitShareChange(input) {
  const response = await fetch('/api/v3/patient-shares', {
    method: 'POST', headers: await getAuthHeaders(), body: JSON.stringify(input),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Unable to update sharing');
  reindexSharingInSearch(input.patientId);
  return result.id;
}

// Identity, company, consent audit and notification are server-owned.
export const createPatientShare = shareData => submitShareChange({
  operation: 'create', patientId: shareData.patientId,
  sharedWithUserId: shareData.sharedWithUserId, accessLevel: shareData.accessLevel,
  consentAttested: shareData.consentAttested === true,
});
export const updateShareAccessLevel = (shareId, accessLevel, patientId) =>
  submitShareChange({ operation: 'update', shareId, patientId, accessLevel });
export const revokePatientShare = (shareId, patientId) =>
  submitShareChange({ operation: 'revoke', shareId, patientId });
/**
 * Get all active shares for a specific patient (owned by the current user)
 * @param {string} patientId - The patient ID
 * @param {string} ownerId - The owner's user ID (required for Firestore security rules)
 * @returns {Promise<Array>} Array of active share records
 */
export const getSharesForPatient = async (patientId, ownerId) => {
  try {
    const sharesRef = collection(db, 'patientShares');
    const q = query(
      sharesRef,
      where('patientId', '==', patientId),
      where('ownerId', '==', ownerId),
      where('revokedAt', '==', null)
    );
    
    const querySnapshot = await getDocs(q);
    const now = new Date();
    
    // Filter out expired shares (can't do compound queries with != and < in Firestore)
    const shares = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(share => {
        if (!share.expiresAt) return true;
        const expiresAt = share.expiresAt.toDate ? share.expiresAt.toDate() : new Date(share.expiresAt);
        return expiresAt > now;
      });
    
    return shares;
  } catch (error) {
    console.error("Error getting shares for patient:", error);
    throw error;
  }
};

/**
 * Get all patients shared with a specific user (active shares only)
 * @param {string} userId - The user ID receiving shares
 * @returns {Promise<Array>} Array of share records with patient data
 */
export const getSharedPatientsForUser = async (userId) => {
  try {
    const sharesRef = collection(db, 'patientShares');
    const q = query(
      sharesRef,
      where('sharedWithUserId', '==', userId),
      where('revokedAt', '==', null)
    );
    
    const querySnapshot = await getDocs(q);
    const now = new Date();
    
    // Filter out expired shares and fetch patient data
    const shares = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(share => {
        if (!share.expiresAt) return true;
        const expiresAt = share.expiresAt.toDate ? share.expiresAt.toDate() : new Date(share.expiresAt);
        return expiresAt > now;
      });
    
    return shares;
  } catch (error) {
    console.error("Error getting shared patients for user:", error);
    throw error;
  }
};

/**
 * Check if a user has an active share for a patient
 * @param {string} userId - The user ID to check
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object|null>} The share record if active, null otherwise
 */
export const getActiveShareForUser = async (userId, patientId) => {
  try {
    const sharesRef = collection(db, 'patientShares');
    const q = query(
      sharesRef,
      where('sharedWithUserId', '==', userId),
      where('patientId', '==', patientId),
      where('revokedAt', '==', null)
    );
    
    const querySnapshot = await getDocs(q);
    const now = new Date();
    
    // Find first non-expired share
    for (const doc of querySnapshot.docs) {
      const share = { id: doc.id, ...doc.data() };
      if (!share.expiresAt) return share;
      const expiresAt = share.expiresAt.toDate ? share.expiresAt.toDate() : new Date(share.expiresAt);
      if (expiresAt > now) return share;
    }
    
    return null;
  } catch (error) {
    console.error("Error checking share for user:", error);
    throw error;
  }
};

/**
 * Get all users in the same company (for share recipient picker)
 * @param {string} companyId - The company ID
 * @param {string} excludeUserId - User ID to exclude (current user)
 * @returns {Promise<Array>} Array of user records
 */
export const getUsersInCompany = async (companyId, excludeUserId = null) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('companyId', '==', companyId)
    );
    
    const querySnapshot = await getDocs(q);
    const users = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(user => user.id !== excludeUserId);
    
    return users;
  } catch (error) {
    console.error("Error getting users in company:", error);
    throw error;
  }
};

/**
 * Check access level permissions
 * @param {string} accessLevel - The access level to check
 * @returns {Object} Permission flags
 */
export const getAccessPermissions = (accessLevel) => {
  return {
    canView: ['view', 'view_add', 'view_add_private', 'full'].includes(accessLevel),
    canAddNotes: ['view_add', 'view_add_private', 'full'].includes(accessLevel),
    canEditOwnDocs: ['view_add_private', 'full'].includes(accessLevel),
    canEdit: accessLevel === 'full'
  };
};

/**
 * Check if a user can perform an action on a shared patient
 * @param {Object} shareInfo - The share info from the patient object
 * @param {string} action - The action to check: 'view', 'add', 'edit', 'editOwn'
 * @returns {boolean} Whether the action is allowed
 */
export const canPerformAction = (shareInfo, action) => {
  if (!shareInfo) return true; // Not a shared patient, allow all actions
  
  const permissions = getAccessPermissions(shareInfo.accessLevel);
  
  switch (action) {
    case 'view':
      return permissions.canView;
    case 'add':
      return permissions.canAddNotes;
    case 'editOwn':
      return permissions.canEditOwnDocs;
    case 'edit':
      return permissions.canEdit;
    default:
      return false;
  }
};

/**
 * Build authorship metadata for a new patient document.
 *
 * Always stamps `createdBy` with the creator's uid — Firestore rules depend on
 * it for the "View + Add (Private)" sharing model (owner cannot edit docs
 * created by a shared user). When writing to a shared patient, also stamps
 * `createdOnBehalfOf: shareInfo.ownerId` so the UI can render "by {user}" and
 * audit can resolve the owning patient record.
 *
 * @param {string} creatorUserId - UID of the user creating the document.
 * @param {Object} [shareInfo] - Share info when writing to a shared patient.
 * @returns {Object} Metadata fields to merge into the document payload.
 */
export const getSharedDocumentMetadata = (creatorUserId, shareInfo = null) => {
  if (typeof creatorUserId !== "string" || !creatorUserId) {
    console.warn("[sharingService] getSharedDocumentMetadata: creatorUserId missing or non-string; document will not carry createdBy");
    return {};
  }
  const metadata = { createdBy: creatorUserId };
  if (shareInfo && shareInfo.ownerId) {
    metadata.createdOnBehalfOf = shareInfo.ownerId;
  }
  return metadata;
};
